import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import TabletMacOutlinedIcon from "@mui/icons-material/TabletMacOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import { useSelector } from "react-redux";
import garahexLogo from "../assets/garahex-logo-white.png";
import { ActionButton, PageHeader, SectionHeader, StatusBadge, SurfaceCard } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";

const PUBLIC_BASE_URL = "https://garahex.com";

const previewModes = [
  { key: "desktop", label: "Desktop", icon: <DesktopWindowsOutlinedIcon fontSize="small" />, width: "100%" },
  { key: "tablet", label: "Tablet", icon: <TabletMacOutlinedIcon fontSize="small" />, width: { xs: "100%", lg: 560 } },
  { key: "mobile", label: "Mobile", icon: <PhoneIphoneOutlinedIcon fontSize="small" />, width: { xs: "100%", lg: 350 } }
];

const pageConfig = {
  landing: {
    title: "Landing Page Settings",
    route: "/",
    type: "Marketing page",
    description: "Preview the current homepage structure: hero, quick quote, services, how-it-works, CTA, and footer.",
    initial: {
      headline: "Deliver Smarter. Move Faster.",
      subtitle: "Connect with trusted logistics providers for your cargo, anytime, anywhere in the Philippines.",
      primaryCta: "Book a Shipment",
      secondaryCta: "Become a Fleet Partner",
      sectionFocus: "Hero"
    },
    fields: [
      { key: "headline", label: "Headline" },
      { key: "subtitle", label: "Subtitle", multiline: true },
      { key: "primaryCta", label: "Primary CTA" },
      { key: "secondaryCta", label: "Secondary CTA" },
      { key: "sectionFocus", label: "Preview Section", select: ["Hero", "Quick Quote", "Services", "How It Works", "CTA", "Footer"] }
    ]
  },
  about: {
    title: "About Us Settings",
    route: "/about-us",
    type: "Brand page",
    description: "Preview the existing About Us public page structure and company-story presentation.",
    initial: {
      headline: "About Us",
      subtitle: "Garahex connects people, cargo, and logistics opportunities across the Philippines.",
      story: "Preview the company story and public About page content.",
      sectionFocus: "Company Story"
    },
    fields: [
      { key: "headline", label: "Hero Title" },
      { key: "subtitle", label: "Subtitle", multiline: true },
      { key: "story", label: "Company Story Preview", multiline: true },
      { key: "sectionFocus", label: "Preview Section", select: ["Hero", "Company Story", "Mission", "Vision", "Values", "CTA"] }
    ]
  },
  contact: {
    title: "Contact Us Settings",
    route: "/contact-us",
    type: "Support page",
    description: "Preview contact details and social/contact sections without adding a map or office-map area.",
    initial: {
      headline: "Contact Us",
      subtitle: "Reach the Garahex support team for booking, fleet, and cargo assistance.",
      phone: "",
      email: "",
      address: "",
      sectionFocus: "Contact Details"
    },
    fields: [
      { key: "headline", label: "Hero Title" },
      { key: "subtitle", label: "Subtitle", multiline: true },
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "address", label: "Address", multiline: true },
      { key: "sectionFocus", label: "Preview Section", select: ["Hero", "Contact Details", "Phone", "Email", "Social Links"] }
    ]
  },
  terms: {
    title: "Terms & Conditions Settings",
    route: "/term-condition",
    type: "Legal document",
    description: "Preview Terms as a continuous readable legal document, not cards or marketing tiles.",
    legal: true,
    initial: {
      headline: "Terms & Conditions",
      lastUpdated: "",
      content: "Terms content preview only. Website content persistence is not configured.",
      sectionFocus: "Document"
    },
    fields: [
      { key: "headline", label: "Page Title" },
      { key: "lastUpdated", label: "Last Updated" },
      { key: "content", label: "Document Content", multiline: true, minRows: 8 }
    ]
  },
  privacy: {
    title: "Privacy Policy Settings",
    route: "/privacy-policy",
    type: "Legal document",
    description: "Preview Privacy Policy as a continuous readable legal document.",
    legal: true,
    initial: {
      headline: "Privacy Policy",
      lastUpdated: "",
      content: "Privacy policy content preview only. Website content persistence is not configured.",
      sectionFocus: "Document"
    },
    fields: [
      { key: "headline", label: "Page Title" },
      { key: "lastUpdated", label: "Last Updated" },
      { key: "content", label: "Document Content", multiline: true, minRows: 8 }
    ]
  },
  branding: {
    title: "Branding Settings",
    route: "/",
    type: "Brand system",
    description: "Preview the existing Garahex logo and established admin color system.",
    branding: true,
    initial: {
      headline: "GARAHEX",
      subtitle: "GARAHE EXPRESS",
      sectionFocus: "Brand Preview"
    },
    fields: [
      { key: "headline", label: "Brand" },
      { key: "subtitle", label: "Tagline" },
      { key: "sectionFocus", label: "Preview Surface", select: ["Dark Surface", "Light Surface", "Header", "Footer", "Button", "Status Accent"] }
    ]
  },
  settings: {
    title: "Website Settings",
    route: "/website-management",
    type: "Settings architecture",
    description: "Preview website settings categories only. No secrets, backend persistence, or production writes are added.",
    initial: {
      headline: "Website Settings",
      subtitle: "General, navigation, branding, contact information, social links, and page management.",
      sectionFocus: "General"
    },
    fields: [
      { key: "headline", label: "Settings Title" },
      { key: "subtitle", label: "Settings Summary", multiline: true },
      { key: "sectionFocus", label: "Category", select: ["General", "Branding", "Navigation", "Contact Information", "Social Links", "Page Management"] }
    ]
  }
};

const colorTokens = [
  ["Midnight", "#031D36"],
  ["Deep Navy", "#062B4F"],
  ["Royal Blue", "#075EA8"],
  ["Electric Blue", "#0B7CFF"],
  ["Garahex Orange", "#FF6B00"],
  ["Warm Orange", "#FF8A1F"],
  ["Success Green", "#16B364"]
];

const liveUrlFor = (route) => `${PUBLIC_BASE_URL}${route === "/" ? "/" : route}`;

const Notice = () => (
  <Box sx={{ border: `1px solid rgba(11, 124, 255, 0.20)`, background: "rgba(11, 124, 255, 0.08)", borderRadius: 4, p: 1.6, mb: 2 }}>
    <Typography sx={{ color: adminTokens.colors.deepNavy, fontWeight: 900 }}>
      Preview only — website content persistence is not configured.
    </Typography>
    <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, mt: 0.4 }}>
      Changes on this screen are temporary local preview state and disappear on reload.
    </Typography>
  </Box>
);

const FieldControl = ({ field, value, onChange }) => {
  if (field.select) {
    return (
      <TextField select fullWidth label={field.label} value={value || ""} onChange={(event) => onChange(field.key, event.target.value)}>
        {field.select.map((option) => (
          <MenuItem key={option} value={option}>{option}</MenuItem>
        ))}
      </TextField>
    );
  }
  return (
    <TextField
      fullWidth
      label={field.label}
      value={value || ""}
      multiline={field.multiline}
      minRows={field.minRows || (field.multiline ? 3 : undefined)}
      onChange={(event) => onChange(field.key, event.target.value)}
    />
  );
};

const BrandPreview = ({ values }) => (
  <Box sx={{ display: "grid", gap: 1.5 }}>
    <Grid container spacing={1.4}>
      {["Dark Surface", "Light Surface", "Header", "Footer"].map((surface) => (
        <Grid item xs={12} sm={6} key={surface}>
          <Box sx={{ minHeight: 132, borderRadius: 4, p: 2, background: surface === "Light Surface" ? "#FFFFFF" : `linear-gradient(135deg, ${adminTokens.colors.deepNavy}, #0B4FA3)`, border: `1px solid ${adminTokens.colors.border}`, color: surface === "Light Surface" ? adminTokens.colors.deepNavy : "#FFFFFF" }}>
            <img src={garahexLogo} alt="Garahex logo" style={{ width: 138, height: "auto", display: "block" }} />
            <Typography sx={{ mt: 1.2, fontWeight: 950 }}>{surface}</Typography>
            <Typography sx={{ opacity: 0.78, fontSize: 13 }}>{values.headline} · {values.subtitle}</Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {colorTokens.map(([name, value]) => (
        <Box key={name} aria-label={`${name} ${value}`} sx={{ display: "flex", alignItems: "center", gap: 0.8, border: `1px solid ${adminTokens.colors.border}`, borderRadius: 999, px: 1.2, py: 0.8, background: "#FFFFFF" }}>
          <Box sx={{ width: 18, height: 18, borderRadius: "50%", background: value }} />
          <Typography sx={{ fontSize: 13, fontWeight: 850 }}>{name}</Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const PagePreview = ({ config, values, mode }) => {
  if (config.branding) return <BrandPreview values={values} />;

  return (
    <Box
      sx={{
        width: mode.width,
        maxWidth: "100%",
        mx: "auto",
        borderRadius: 5,
        overflow: "hidden",
        border: `1px solid ${adminTokens.colors.border}`,
        boxShadow: "0 24px 56px rgba(3, 29, 54, 0.12)",
        transition: "width 180ms ease, opacity 160ms ease",
        "@media (prefers-reduced-motion: reduce)": { transition: "none" }
      }}
    >
      <Box sx={{ background: `linear-gradient(135deg, ${adminTokens.colors.deepNavy}, #0B4FA3)`, color: "#FFFFFF", p: 2 }}>
        <Box sx={{ width: 138, mb: 2 }}>
          <img src={garahexLogo} alt="Garahex logo" style={{ display: "block", width: "100%", height: "auto" }} />
        </Box>
        <Typography component="h2" sx={{ fontWeight: 950, fontSize: { xs: "1.55rem", md: "2rem" }, lineHeight: 1.05 }}>
          {values.headline}
        </Typography>
        {!config.legal ? (
          <Typography sx={{ mt: 1, color: "rgba(255,255,255,.80)", maxWidth: 640 }}>{values.subtitle}</Typography>
        ) : null}
        <Box sx={{ mt: 1.6, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <StatusBadge tone="warning">{config.type}</StatusBadge>
          <StatusBadge tone="info">{config.route}</StatusBadge>
        </Box>
      </Box>
      <Box sx={{ p: { xs: 2, md: 2.5 }, background: "#FFFFFF", minHeight: 240 }}>
        {config.legal ? (
          <Box sx={{ maxWidth: 760, mx: "auto" }}>
            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850, mb: 1 }}>
              {values.lastUpdated ? `Last Updated: ${values.lastUpdated}` : "Last Updated: Preview only"}
            </Typography>
            {String(values.content || "").split(/\n+/).filter(Boolean).map((paragraph, index) => (
              <Typography key={`${paragraph}-${index}`} sx={{ color: adminTokens.colors.text, lineHeight: 1.75, mb: 1.4 }}>
                {paragraph}
              </Typography>
            ))}
          </Box>
        ) : (
          <>
            <Typography sx={{ color: adminTokens.colors.deepNavy, fontWeight: 950, fontSize: "1.2rem" }}>{values.sectionFocus}</Typography>
            <Typography sx={{ color: adminTokens.colors.muted, mt: 1 }}>{values.story || values.address || values.subtitle}</Typography>
            <Grid container spacing={1.2} sx={{ mt: 1.4 }}>
              {["Live route", "Preview state", "No persistence"].map((item, index) => (
                <Grid item xs={12} sm={4} key={item}>
                  <Box sx={{ border: `1px solid ${adminTokens.colors.border}`, borderRadius: 3, p: 1.4, background: index === 0 ? "#F7FBFF" : "#FFFFFF" }}>
                    <Typography sx={{ color: adminTokens.colors.deepNavy, fontWeight: 900 }}>{item}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
};

export default function WebsitePageSettings() {
  const { pageKey = "landing" } = useParams();
  const settings = useSelector((state) => state.settingsdata.settings) || {};
  const config = pageConfig[pageKey] || pageConfig.landing;
  const seededInitial = useMemo(() => {
    if (pageKey !== "contact") return config.initial;
    return {
      ...config.initial,
      phone: settings.CompanyPhone || "",
      email: settings.contact_email || "",
      address: settings.CompanyAddress || ""
    };
  }, [config, pageKey, settings.CompanyAddress, settings.CompanyPhone, settings.contact_email]);
  const [values, setValues] = useState(seededInitial);
  const [previewMode, setPreviewMode] = useState("desktop");
  const selectedMode = previewModes.find((mode) => mode.key === previewMode) || previewModes[0];

  useEffect(() => {
    setValues(seededInitial);
    setPreviewMode("desktop");
  }, [seededInitial]);

  const updateValue = (key, value) => setValues((prev) => ({ ...prev, [key]: value }));
  const resetPreview = () => setValues(seededInitial);

  return (
    <Box className="garahex-website-page-settings">
      <PageHeader
        eyebrow="Website"
        title={config.title}
        description={config.description}
        actions={
          <>
            <Button component={Link} to="/website-management" variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}>
              Back to Website Management
            </Button>
            <Button component="a" href={liveUrlFor(config.route)} target="_blank" rel="noopener noreferrer" variant="outlined" endIcon={<OpenInNewIcon />} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}>
              Open Live Page
            </Button>
          </>
        }
      />
      <Notice />
      <Grid container spacing={2.2} alignItems="flex-start">
        <Grid item xs={12} lg={5}>
          <SurfaceCard>
            <SectionHeader
              title="Editor Panel"
              description="Temporary local preview controls only. There is intentionally no Save or Publish action."
              actions={<StatusBadge tone="warning">Preview Only</StatusBadge>}
            />
            <Box sx={{ display: "grid", gap: 1.6 }}>
              {config.fields.map((field) => (
                <FieldControl key={field.key} field={field} value={values[field.key]} onChange={updateValue} />
              ))}
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
              <ActionButton type="button">Preview Changes</ActionButton>
              <Button type="button" onClick={resetPreview} variant="outlined" startIcon={<RestartAltIcon />} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}>
                Reset Preview
              </Button>
            </Box>
          </SurfaceCard>
        </Grid>
        <Grid item xs={12} lg={7}>
          <SurfaceCard>
            <SectionHeader
              title="Preview Panel"
              description={`Previewing ${config.route} as a ${config.type.toLowerCase()}.`}
              actions={<StatusBadge tone="info">{config.route}</StatusBadge>}
            />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              {previewModes.map((mode) => (
                <Button
                  key={mode.key}
                  onClick={() => setPreviewMode(mode.key)}
                  startIcon={mode.icon}
                  variant={previewMode === mode.key ? "contained" : "outlined"}
                  sx={{ borderRadius: 999, textTransform: "none", fontWeight: 850 }}
                >
                  {mode.label}
                </Button>
              ))}
            </Box>
            <PagePreview config={config} values={values} mode={selectedMode} />
          </SurfaceCard>
        </Grid>
      </Grid>
    </Box>
  );
}
